import { useState, useEffect, useRef } from "react";
import sharedAPIsService from "../sharedAPIs";
import { fetchDashboardDataBatch } from "../../helpers/util";
import { FetchDashboardDataParams } from "../../consts/shared";
import { pagesFilterOptionsData, pagesFilterOptionsDataTypes } from "../../helpers/pagesUtil";

interface LastSyncResponse {
  data: {
    lastSyncDateFormatted: string;
  };
}

const useFetchFiltersData = ({ filters }: FetchDashboardDataParams) => {
  const [filtersData, setFiltersData] = useState<pagesFilterOptionsDataTypes>(pagesFilterOptionsData);
  const hasLoadedOrganisations = useRef(false);

  // Initial load: fetch all filters including organisations and last sync date
  useEffect(() => {
    
    const fetchAllFilters = async () => {
      const apiMap = {
        getSitesNames: () => sharedAPIsService.getSitesNames(filters),
        getRolesNames: () => sharedAPIsService.getRolesNames(filters),
        getCoursesNames: () => sharedAPIsService.getCoursesNames(filters),
        getLastSyncedDate: () => sharedAPIsService.getLastSyncedDate(filters),
        getOrganisationsNames: () => sharedAPIsService.getOrganisationsNames(filters),
        getUsersNames: () => sharedAPIsService.getUsersNames(filters),
        getAuthorsNames: () => sharedAPIsService.getAuthorsNames(filters),
      };

      try {
        const resultData = await fetchDashboardDataBatch(apiMap);
        
        setFiltersData(prevState => ({
          ...prevState,
          Site: resultData.getSitesNames?.data,
          Role: resultData.getRolesNames?.data,
          Courses: resultData.getCoursesNames?.data,
          Organisations: resultData.getOrganisationsNames?.data,
          lastSyncedDate: (resultData.getLastSyncedDate as unknown as LastSyncResponse)?.data?.lastSyncDateFormatted,
          Users: resultData.getUsersNames?.data,
          Authors: resultData.getAuthorsNames?.data,
        }));
        
        hasLoadedOrganisations.current = true;
      } catch (error) {
        if ((error as any)?.name !== "CanceledError") {
          console.error("Initial filters data fetch failed:", error);
        }
      }
    };
    
    fetchAllFilters();
  }, []);

  // Fetch site, role, course, users when organisation changes or filters clear
  useEffect(() => {
    // Skip on initial mount - initial load already fetches all data
    if (!hasLoadedOrganisations.current) {
      return;
    }
    
    const fetchOrganisationRelatedData = async () => {
      const apiMap = {
        getSitesNames: () => sharedAPIsService.getSitesNames(filters),
        getRolesNames: () => sharedAPIsService.getRolesNames(filters),
        getCoursesNames: () => sharedAPIsService.getCoursesNames(filters),
        getUsersNames: () => sharedAPIsService.getUsersNames(filters),
        getAuthorsNames: () => sharedAPIsService.getAuthorsNames(filters),
      };

      try {
        const resultData = await fetchDashboardDataBatch(apiMap);
        
        setFiltersData(prevState => ({
          ...prevState,
          Site: resultData.getSitesNames?.data,
          Role: resultData.getRolesNames?.data,
          Courses: resultData.getCoursesNames?.data,
          Users: resultData.getUsersNames?.data,
          Authors: resultData.getAuthorsNames?.data,
        }));
      } catch (error) {
        if ((error as any)?.name !== "CanceledError") {
          console.error("Organisation-related data fetch failed:", error);
        }
      }
    };
    
    fetchOrganisationRelatedData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters?.Organisations]);

/*
  // Fetch users when roles or site change
  useEffect(() => {
    
    const fetchUsersData = async () => {
      const apiMap = {
        getUsersNames: () => sharedAPIsService.getUsersNames(filters),
        getAuthorsNames: () => sharedAPIsService.getAuthorsNames(filters),
      };

      try {
        const resultData = await fetchDashboardDataBatch(apiMap);
        setFiltersData(prevState => ({ 
          ...prevState, 
          Users: resultData.getUsersNames?.data,
          Authors: resultData.getAuthorsNames?.data,
        }));
      } catch (error) {
        if ((error as any)?.name !== "CanceledError") {
          console.error("Users data fetch failed:", error);
        }
      }
    };
    
    fetchUsersData();
  }, [filters?.Role, filters?.Site]);
*/

  return { filtersData, setFiltersData };
};

export default useFetchFiltersData;
